import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, RotateCcw, Download, MoreHorizontal } from 'lucide-react';
import { HIGHLIGHT_COLORS } from '@jobos/shared/types/job';
import type { FilterProfile } from '@jobos/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { JobPhaseSelector } from './JobPhaseSelector';
import type { JobPhase } from './JobPhaseSelector';
import { JobListSortMenu } from './JobListSortMenu';
import { IncludeArchivedCheckbox } from './filters/IncludeArchivedCheckbox';
import { HIGHLIGHT_COLOR_MAP } from '../utils/highlightColors';

/** Matches JobFileCaptureBar — the mobile pass's minimum tap target. */
const TOUCH_TARGET = 'min-h-[44px]';

interface JobListToolbarProps {
  filterProfiles: FilterProfile[];
  activeProfileName: string;
  isProfileModified: boolean;
  onProfileChange: (name: string) => void;
  onClearFilters: () => void;
  onResetToProfile: () => void;
  onOpenDrawer: () => void;
  selectedPhase: JobPhase;
  onPhaseChange: (phase: JobPhase) => void;
  showArchived: boolean | 'only';
  showDeleted: boolean;
  onArchivedChange: (value: boolean | 'only') => void;
  searchText: string;
  onSearchChange: (value: string) => void;
  isFetching: boolean;
  isLoading: boolean;
  highlightFilter: string | null;
  onHighlightFilterChange: (color: string | null) => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort: (field: string) => void;
}

export function JobListToolbar({
  filterProfiles,
  activeProfileName,
  isProfileModified,
  onProfileChange,
  onClearFilters,
  onResetToProfile,
  onOpenDrawer,
  selectedPhase,
  onPhaseChange,
  showArchived,
  showDeleted,
  onArchivedChange,
  searchText,
  onSearchChange,
  isFetching,
  isLoading,
  highlightFilter,
  onHighlightFilterChange,
  sortField,
  sortDirection,
  onSort,
}: JobListToolbarProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const response = await fetch(`${apiBaseUrl}/jobs/export/excel`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to export jobs');
      }

      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'JobOS_Jobs_Export.xlsx';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export jobs to Excel');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="mb-8">
      {/* Mobile Layout — below md, which is where the cards take over from the table */}
      <div
        data-testid="job-list-toolbar-mobile"
        className="flex flex-col gap-3 md:hidden pb-6 border-b border-gray-200"
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by job code or address..."
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`pl-9 pr-9 ${TOUCH_TARGET}`}
          />
          {isFetching && !isLoading && (
            <Loader2 className="absolute right-3 top-1/2 -mt-2 h-4 w-4 animate-spin text-gray-400" />
          )}
        </div>

        <JobPhaseSelector value={selectedPhase} onChange={onPhaseChange} />

        <div className="flex gap-2">
          <Button variant="outline" onClick={onOpenDrawer} className={`${TOUCH_TARGET} flex-1`}>
            Filter
          </Button>
          <JobListSortMenu
            selectedPhase={selectedPhase}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            className="flex-1 min-w-0"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                aria-label="More actions"
                className={`${TOUCH_TARGET} min-w-[44px] px-0`}
              >
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem
                className={TOUCH_TARGET}
                onClick={handleExportToExcel}
                // The item is a div, not a button — the primitive greys it out and
                // kills pointer events off this data attribute, not a disabled prop.
                data-disabled={isExporting ? '' : undefined}
                aria-disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export to Excel
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs font-medium text-gray-500">Filter profiles</div>
              {filterProfiles.map((profile) => {
                const isActive = profile.name === activeProfileName;
                return (
                  <DropdownMenuItem
                    key={profile.name}
                    className={`${TOUCH_TARGET} ${isActive ? 'font-semibold' : ''}`}
                    onClick={() => onProfileChange(profile.name)}
                  >
                    {profile.name}
                    {profile.isDefault && ' (Default)'}
                    {isActive && isProfileModified && ' *'}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuItem className={TOUCH_TARGET} onClick={onClearFilters}>
                Clear Filters
              </DropdownMenuItem>
              {isProfileModified && (
                <DropdownMenuItem className={TOUCH_TARGET} onClick={onResetToProfile}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset to saved profile
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-1">
          {HIGHLIGHT_COLORS.map((color) => {
            const config = HIGHLIGHT_COLOR_MAP[color];
            const isActive = highlightFilter === color;
            return (
              <button
                key={color}
                type="button"
                onClick={() => onHighlightFilterChange(isActive ? null : color)}
                // 44px of hit area around an 18px dot: the target is the padding,
                // not the paint.
                className={`${TOUCH_TARGET} min-w-[44px] flex items-center justify-center rounded-md`}
                aria-label={`Filter by ${color}`}
                aria-pressed={isActive}
                title={`Filter by ${color}`}
              >
                <span
                  className={`block w-[18px] h-[18px] rounded-full border-2 transition-transform ${isActive ? 'scale-125' : ''}`}
                  style={{
                    backgroundColor: config.bg,
                    borderColor: isActive ? config.border : 'transparent',
                  }}
                />
              </button>
            );
          })}
          {highlightFilter && (
            <button
              type="button"
              onClick={() => onHighlightFilterChange(null)}
              className={`${TOUCH_TARGET} min-w-[44px] text-sm text-gray-400`}
              aria-label="Clear highlight filter"
              title="Clear highlight filter"
            >
              ✕
            </button>
          )}
        </div>

        <IncludeArchivedCheckbox
          showArchived={showArchived}
          showDeleted={showDeleted}
          onArchivedChange={onArchivedChange}
        />

        <Link to="/jobs/new" className="w-full">
          <Button className={`w-full ${TOUCH_TARGET}`}>New Job</Button>
        </Link>
      </div>

      {/* Desktop Layout — wraps rather than overflowing once the window narrows */}
      <div
        data-testid="job-list-toolbar-desktop"
        className="hidden md:flex flex-wrap justify-between items-center gap-y-3"
      >
        <div className="flex flex-wrap items-center gap-3 gap-y-3">
          <Button variant="outline" onClick={onOpenDrawer}>Filter Jobs</Button>
          <select
            value={activeProfileName}
            onChange={(e) => {
              if (e.target.value === '__clear__') {
                onClearFilters();
              } else {
                onProfileChange(e.target.value);
              }
            }}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="">Filter Profiles</option>
            {filterProfiles.map((profile) => {
              const isActive = profile.name === activeProfileName;
              const showModified = isActive && isProfileModified;
              let label = profile.name;
              if (profile.isDefault) label += ' (Default)';
              if (showModified) label += ' *';
              return (
                <option key={profile.name} value={profile.name}>
                  {label}
                </option>
              );
            })}
            <option value="__clear__">Clear Filters</option>
          </select>
          {isProfileModified && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetToProfile}
              className="px-2"
              title="Reset to saved profile"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          {/* <JobPhaseSelector value={selectedPhase} onChange={onPhaseChange} /> */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by job code or address..."
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 pr-9"
            />
            {isFetching && !isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -mt-2 h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {HIGHLIGHT_COLORS.map((color) => {
              const config = HIGHLIGHT_COLOR_MAP[color];
              const isActive = highlightFilter === color;
              return (
                <button
                  key={color}
                  onClick={() => onHighlightFilterChange(isActive ? null : color)}
                  className={`w-[18px] h-[18px] rounded-full border-2 transition-transform hover:scale-110 ${isActive ? 'scale-110' : ''}`}
                  style={{
                    backgroundColor: config.bg,
                    borderColor: isActive ? config.border : 'transparent',
                  }}
                  title={`Filter by ${color}`}
                />
              );
            })}
            {highlightFilter && (
              <button
                onClick={() => onHighlightFilterChange(null)}
                className="text-xs text-gray-400 hover:text-gray-600 ml-1"
                title="Clear highlight filter"
              >
                ✕
              </button>
            )}
          </div>
          <IncludeArchivedCheckbox
            showArchived={showArchived}
            showDeleted={showDeleted}
            onArchivedChange={onArchivedChange}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportToExcel}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export to Excel
          </Button>
          <Link to="/jobs/new">
            <Button>Create New Job</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
